// 1:1 wrappers around the Withings webservice program endpoints.

import { ws } from '@/lib/wiservice';

/**
 * A program (care team) the current user is enrolled in.
 * Shape matches the `programs[]` element returned by `program.getenrolled`.
 */
export interface ProgramData {
  programid: string | number;
  name?: string;
  cohortid?: string | number;
  cohort_name?: string;
  description?: string;
}

interface GetEnrolledBody {
  programs: ProgramData[];
}

/**
 * Programs (care teams) the current user is enrolled in.
 * Mirrors wpc-front `models/Program.js getEnrolled`.
 * Returns an empty array rather than throwing on no enrollment so the
 * caller can render an empty-state page.
 */
export async function getEnrolled(): Promise<ProgramData[]> {
  const response = await ws<GetEnrolledBody>('program', 'getenrolled', {});
  return response.body.programs ?? [];
}

interface GetByIdBody {
  program: ProgramData;
}

/**
 * A single program (care team) by id. `ws()` throws `WsError` when the caller
 * lacks rights (status 214 WRONGPERM) or the program doesn't exist / is
 * inactive (380 OBJECT_NOTFOUND) — the two states the landing page branches on.
 * Success alone proves access (the body carries metadata, not the caller's role).
 */
export async function getById(programid: string | number): Promise<ProgramData> {
  // `ws()` normalizes a non-OK WS status to `WsError.status` centrally, so a
  // 214 (WRONGPERM) / 380 (OBJECT_NOTFOUND) surfaces as `error.status` here.
  const response = await ws<GetByIdBody>('program', 'get', { programid: String(programid) });
  return response.body.program;
}
